import { Head, router } from '@inertiajs/react'
import { Fragment, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { SegmentedControl, Text } from '@radix-ui/themes'
import { List, Search } from 'lucide-react'
import LocationSearch, { DEFAULT_DISTANCE } from '@/shared/map_index/LocationSearch'

declare const mapboxgl: any

const MAPBOX_TOKEN = "pk.eyJ1IjoiamFtZXNtc2tlbHNleTE5ODMiLCJhIjoiY21jaDVtNWIzMHB6YjJycHc5MzMzdmZpayJ9.zlHddGmigDWj5Iw44Q7uyw"

const MINE_COLOR = '#eab308'
// Everything that isn't yours is the brand accent. Mapbox paints on a canvas
// and can't take a CSS var, so it's resolved off the map's container (inside
// the Theme, where --accent-9 exists) when the layer is built — then painted
// onto a pixel and read back, because on P3 screens the var is an oklch()
// Mapbox can't parse.
const accentColor = (el: HTMLElement) => {
  const ctx = document.createElement('canvas').getContext('2d')
  const value = getComputedStyle(el).getPropertyValue('--accent-9').trim()
  if (!ctx || !value) return '#ff2e89'
  ctx.fillStyle = value
  ctx.fillRect(0, 0, 1, 1)
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data
  return `rgb(${r}, ${g}, ${b})`
}
const DEFAULT_CENTER: [number, number] = [-96, 38]

/** One dot on the map. `mine` paints it in the "yours" colour. */
export type MapMarker = {
  mine: boolean
  longitude: number
  latitude: number
  popupHtml: string
}

/** A labelled group in the results rail; empty groups render nothing. */
export type MapListSection<T> = {
  label: string
  items: T[]
}

type MapListIndexProps<T> = {
  title: string
  /** Inertia path that search/locate submit back to, e.g. "/tracks". Query
   *  params on it ("/events?view=map") survive a search. */
  path: string
  /** Above the search in the rail — the events index's List/Map toggle. */
  toolbar?: ReactNode
  markers: MapMarker[]
  sections: MapListSection<T>[]
  emptyMessage: string
  mapCenter: [number, number] | null
  zoom: number
  initialSearch: string
  initialDistance: string
  /** Where the map flies to when a result is clicked. */
  coordsFor: (item: T) => [number, number]
  renderItem: (item: T, state: { selected: boolean; onSelect: () => void }) => ReactNode
}

/**
 * The shared "map beside a searchable list" screen behind /tracks and /events.
 * Owns the layout, the Mapbox instance, the search form and the mobile tabs;
 * callers supply the markers, the grouped results, and how one result renders.
 */
export default function MapListIndex<T extends { id: number }>({
  title,
  path,
  toolbar,
  markers,
  sections,
  emptyMessage,
  mapCenter,
  zoom,
  initialSearch,
  initialDistance,
  coordsFor,
  renderItem,
}: MapListIndexProps<T>) {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const [mobileTab, setMobileTab] = useState<'list' | 'search'>('list')
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const isEmpty = sections.every(section => section.items.length === 0)

  useEffect(() => {
    if (!initialSearch && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(({ coords }) => {
        router.get(path, { search: `${coords.latitude}, ${coords.longitude}`, distance: initialDistance || DEFAULT_DISTANCE })
      })
    }
  }, [])

  const center: [number, number] = mapCenter || DEFAULT_CENTER

  // Both are rebuilt on every render by the caller, so depend on their contents
  // rather than their identity — otherwise the map tears down and remounts
  // constantly, losing pan/zoom.
  const markersKey = JSON.stringify(markers)
  const centerKey = JSON.stringify(mapCenter)

  useEffect(() => {
    if (!mapContainerRef.current) return

    mapboxgl.accessToken = MAPBOX_TOKEN
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      center,
      zoom,
      style: 'mapbox://styles/mapbox/standard-satellite',
    })
    mapRef.current = map

    map.addControl(
      new mapboxgl.NavigationControl({ visualizePitch: true, showZoom: true, showCompass: true }),
      'top-right'
    )

    map.on('load', () => {
      const features = markers.map(marker => ({
        type: 'Feature',
        properties: { mine: marker.mine, description: marker.popupHtml },
        geometry: { type: 'Point', coordinates: [marker.longitude, marker.latitude] }
      }))

      map.addSource('places', {
        type: 'geojson',
        generateId: true,
        data: { type: 'FeatureCollection', features }
      })

      map.addLayer({
        id: 'places',
        type: 'circle',
        source: 'places',
        paint: {
          'circle-color': ['case', ['get', 'mine'], MINE_COLOR, accentColor(map.getContainer())],
          'circle-radius': 8,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      })

      if (mapCenter) {
        map.addSource('your-location', {
          type: 'geojson',
          generateId: true,
          data: {
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              properties: { description: 'Your Location' },
              geometry: { type: 'Point', coordinates: center }
            }]
          }
        })
        map.addLayer({
          id: 'your-location',
          type: 'circle',
          source: 'your-location',
          paint: {
            'circle-color': '#002e89',
            'circle-radius': 8,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff'
          }
        })
      }

      const popup = new mapboxgl.Popup({ closeOnClick: true })

      map.addInteraction('places-mouseenter-interaction', {
        type: 'mouseenter',
        target: { layerId: 'places' },
        handler: (e: any) => {
          map.getCanvas().style.cursor = 'pointer'
          popup.setLngLat(e.feature.geometry.coordinates.slice()).setHTML(e.feature.properties.description).addTo(map)
        }
      })

      map.addInteraction('places-mouseleave-interaction', {
        type: 'mouseleave',
        target: { layerId: 'places' },
        handler: () => { map.getCanvas().style.cursor = '' }
      })

      map.on('click', 'places', (e: any) => {
        map.flyTo({ center: e.features[0].geometry.coordinates, duration: 3000, zoom: 15 })
      })
    })

    return () => {
      map.remove()
    }
  }, [markersKey, centerKey, zoom])

  const selectItem = (item: T) => {
    setSelectedId(item.id)
    if (mapRef.current) {
      mapRef.current.flyTo({ center: coordsFor(item), duration: 3000, zoom: 15 })
    }
  }

  return (
    <>
      <Head title={title} />
      <section>
        <div className="flex flex-col lg:flex-row">

          {/* Map: top on mobile, right column on desktop */}
          <div className="order-1 w-full lg:order-2 lg:w-8/12">
            <div ref={mapContainerRef} className="h-[42vh] w-full lg:h-[calc(100vh-3.5rem)]" />
          </div>

          {/* Controls + list: below the map on mobile, left column on desktop */}
          <div className="order-2 flex w-full flex-col bg-canvas lg:order-1 lg:h-[calc(100vh-3.5rem)] lg:w-4/12">

            {toolbar && <div className="flex-none px-3 pt-3 lg:px-6 lg:pt-6">{toolbar}</div>}

            {/* Mobile tabs (List / Search). lg:hidden on a wrapper: Radix's
                unlayered display rule beats it on the control itself. */}
            <div className="m-3 lg:hidden">
              <SegmentedControl.Root
                value={mobileTab}
                onValueChange={value => setMobileTab(value as 'list' | 'search')}
                size="2"
                style={{ width: '100%' }}
              >
                <SegmentedControl.Item value="list">
                  <span className="flex items-center gap-1"><List className="h-4 w-4" /> List</span>
                </SegmentedControl.Item>
                <SegmentedControl.Item value="search">
                  <span className="flex items-center gap-1"><Search className="h-4 w-4" /> Search</span>
                </SegmentedControl.Item>
              </SegmentedControl.Root>
            </div>

            {/* Search form: mobile shows under the Search tab; always visible on desktop */}
            <div className={`flex-none ${mobileTab === 'search' ? 'block' : 'hidden'} lg:block`}>
              <div className="w-full p-4 lg:p-6">
                <LocationSearch
                  path={path}
                  initialSearch={initialSearch}
                  initialDistance={initialDistance}
                  onSearch={() => setMobileTab('list')}
                />
              </div>
            </div>

            {/* List: mobile shows under the List tab; always visible on desktop */}
            <div className={`${mobileTab === 'list' ? 'block' : 'hidden'} lg:block lg:flex-1 lg:overflow-y-auto`}>
              <div className="w-full p-4 flex flex-col gap-5">
                {sections.map(section => section.items.length > 0 && (
                  <div key={section.label} className="flex flex-col gap-3">
                    <Text as="div" size="2" weight="bold" color="gray" className="px-1 uppercase tracking-wider">
                      {section.label}
                    </Text>
                    {section.items.map(item => (
                      <Fragment key={item.id}>
                        {renderItem(item, {
                          selected: selectedId === item.id,
                          onSelect: () => selectItem(item),
                        })}
                      </Fragment>
                    ))}
                  </div>
                ))}
                {isEmpty && (
                  <Text as="p" color="gray" align="center" className="p-4">{emptyMessage}</Text>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  )
}
